import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { Assessment, FileDownload, ShowChart } from '@mui/icons-material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AnalyticsExportButtonSx } from './dqShared';
import DqFilters from './dqFilters';
import DqQuestionIdCell from './dqQuestionIdCell';
import { downloadCsv, exportTimestamp, formatDisplayDate } from './dqUtils';

const DqChartsSection = ({
  chartTab,
  onChartTabChange,
  answerTrends,
  questions,
  chartsLoading,
  dateRange,
  startDate,
  endDate,
  subscription,
  onDateRangeChange,
  onStartDateChange,
  onEndDateChange,
  onSubscriptionChange,
}) => {
  const exportTrends = () => {
    downloadCsv(
      (answerTrends || []).map((row) => ({
        date: row.date,
        answerCount: row.answerCount,
      })),
      `dq_answer_trends_${exportTimestamp()}.csv`
    );
  };

  const exportQuestions = () => {
    downloadCsv(
      (questions || []).map((row) => ({
        questionId: row.questionId,
        date: row.date,
        attempts: row.attempts,
        accuracy: row.accuracy,
        avgTimeSeconds: row.avgTimeSeconds,
      })),
      `dq_question_performance_${exportTimestamp()}.csv`
    );
  };

  const totalAnswers = (answerTrends || []).reduce((sum, row) => sum + (row.answerCount || 0), 0);

  return (
    <Box sx={{ mt: 3 }}>
      <Paper>
        <Tabs
          value={chartTab}
          onChange={(_e, value) => onChartTabChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="gamification chart tabs"
        >
          <Tab icon={<ShowChart />} label="Answer Trends" iconPosition="top" />
          <Tab icon={<Assessment />} label="Question Performance" iconPosition="top" />
        </Tabs>
      </Paper>

      <DqFilters
        dateRange={dateRange}
        startDate={startDate}
        endDate={endDate}
        subscription={subscription}
        onDateRangeChange={onDateRangeChange}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onSubscriptionChange={onSubscriptionChange}
      />

      <Box sx={{ mt: 2 }}>
        {chartTab === 0 ? (
          <Card sx={{ width: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Answer Trends</Typography>
                <Button
                  variant="contained"
                  startIcon={<FileDownload />}
                  onClick={exportTrends}
                  disabled={chartsLoading}
                  size="small"
                  sx={AnalyticsExportButtonSx}
                >
                  Export Data
                </Button>
              </Box>
              <Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>
                Total answers: {totalAnswers}
              </Typography>
              {chartsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={answerTrends || []} margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      angle={-45}
                      textAnchor="end"
                      height={55}
                      tickFormatter={(v) => formatDisplayDate(v)}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip labelFormatter={(v) => formatDisplayDate(v)} />
                    <Line
                      type="monotone"
                      dataKey="answerCount"
                      name="Answers"
                      stroke="#3f51b5"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ width: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Question Performance</Typography>
                <Button
                  variant="contained"
                  startIcon={<FileDownload />}
                  onClick={exportQuestions}
                  disabled={chartsLoading}
                  size="small"
                  sx={AnalyticsExportButtonSx}
                >
                  Export Data
                </Button>
              </Box>
              {chartsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 420 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Question ID</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Attempts</TableCell>
                        <TableCell align="right">Accuracy (%)</TableCell>
                        <TableCell align="right">Avg time (s)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(questions || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No questions in this period
                          </TableCell>
                        </TableRow>
                      ) : (
                        questions.map((row) => (
                          <TableRow key={`${row.questionIdRaw}-${row.date}`} hover>
                            <TableCell>
                              <DqQuestionIdCell row={row} />
                            </TableCell>
                            <TableCell>{formatDisplayDate(row.date)}</TableCell>
                            <TableCell align="right">{row.attempts}</TableCell>
                            <TableCell align="right">{row.accuracy}</TableCell>
                            <TableCell align="right">{row.avgTimeSeconds}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default DqChartsSection;
